import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Row } from '@vendasta/galaxy/table';
import { CrmFieldService, StandardExternalIds } from '@galaxy/crm/static';
import { EmailProvider } from './send-email/constants';

@Injectable({
  providedIn: 'root',
})
export class EmailWorkflowService {
  private router = inject(Router);
  private crmFieldService = inject(CrmFieldService);

  /**
   * Navigate to email builder with contact IDs and optional email addresses
   * @param namespace The namespace (typically account group ID)
   * @param userId The current user ID
   * @param contactIds Array of contact IDs
   * @param returnUrl The URL to navigate back to after sending/canceling email
   * @param route The route to navigate to (e.g., ['/account/location', namespace, 'crm', 'send-email'])
   * @param emails Array of email addresses (optional)
   * @param provider Email provider to use ('gmail' or 'outlook')
   */
  async navigateToEmailBuilder(
    namespace: string,
    userId: string,
    contactIds: string[],
    returnUrl: string,
    route: string[],
    emails: string[] = [],
    provider: EmailProvider
  ): Promise<void> {
    // Navigate to send email page with contact context
    const queryParams: Record<string, string> = {
      namespace: namespace,
      userId: userId,
      contacts: contactIds.join(','),
      source: 'crm',
      returnUrl: returnUrl,
      provider: provider,
    };
    
    // Only add emails param if we have email addresses
    const validEmails = emails.filter(e => e);
    if (validEmails.length > 0) {
      queryParams['emails'] = validEmails.join(',');
    }

    await this.router.navigate(route, { queryParams });
  }

  /**
   * Navigate to send email page from contact rows
   * @param namespace The namespace (typically account group ID)
   * @param userId The current user ID
   * @param rows Array of contact rows
   * @param returnUrl The URL to navigate back to after sending/canceling email
   * @param route The route to navigate to (e.g., ['/account/location', namespace, 'crm', 'send-email'])
   * @param provider Email provider to use ('gmail' or 'outlook')
   */
  async navigateToEmailBuilderFromRows(
    namespace: string,
    userId: string,
    rows: Row[],
    returnUrl: string,
    route: string[],
    provider: EmailProvider
  ): Promise<void> {
    const contactIds = rows.map(row => row.id);
    
    // Extract email addresses from rows
    const emailFieldId = this.crmFieldService.getFieldId(StandardExternalIds.Email);
    const emails = rows.map(row => {
      const emailField = row.data[emailFieldId];
      return emailField?.value || '';
    });
    
    await this.navigateToEmailBuilder(namespace, userId, contactIds, returnUrl, route, emails, provider);
  }
}

